import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCompare } from '@/contexts/CompareContext';
import { useSettings } from '@/contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Shield, Menu, User, LogOut, Settings } from 'lucide-react';

const FALLBACK_LOGO = '/logo.png';

const Header = () => {
  const { user, logout } = useAuth();
  const { compareList } = useCompare();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Use logo URL directly (external URL) with fallback to local logo
  const logoUrl = settings.logo_url || FALLBACK_LOGO;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/policies', label: 'Policies' },
    { to: '/compare', label: 'Compare', badge: compareList.length > 0 ? compareList.length : null },
    { to: '/my-policies', label: 'My Policies', requireAuth: true },
    { to: '/get-quote', label: 'Get Quote', highlight: true }
  ];

  return (
    <header className="sticky top-0 z-50 w-full bg-white border-b border-secondary-200 shadow-sm" data-testid="header">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={logoUrl} 
              alt={`${settings.company_name} Logo`} 
              className="h-24 w-auto object-contain"
              onError={(e) => { e.target.src = FALLBACK_LOGO; }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.filter(link => !link.requireAuth || user).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-base font-medium transition-colors duration-200 flex items-center gap-2 ${
                  link.highlight 
                    ? 'bg-pink-500 hover:bg-pink-600 text-white px-4 py-2 rounded-lg'
                    : 'text-secondary-700 hover:text-primary-600'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
                {link.badge && (
                  <Badge variant="default" className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {link.badge}
                  </Badge>
                )}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                {user.role === 'admin' && (
                  <Link to="/admin">
                    <Button variant="ghost" size="sm" data-testid="admin-button">
                      <Settings className="mr-2 h-4 w-4" />
                      Admin
                    </Button>
                  </Link>
                )}
                <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button size="sm" data-testid="login-button">
                  <User className="mr-2 h-4 w-4" />
                  Login
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <div className="flex flex-col gap-6 mt-8">
                <Link to="/" className="flex items-center mb-4">
                  <img 
                    src={logoUrl} 
                    alt={`${settings.company_name} Logo`} 
                    className="h-24 w-auto object-contain"
                    onError={(e) => { e.target.src = FALLBACK_LOGO; }}
                  />
                </Link>

                <nav className="flex flex-col gap-4">
                  {navLinks.filter(link => !link.requireAuth || user).map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-lg font-medium text-secondary-700 hover:text-primary-600 transition-colors duration-200 flex items-center gap-2"
                    >
                      {link.label}
                      {link.badge && (
                        <Badge variant="default" className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                          {link.badge}
                        </Badge>
                      )}
                    </Link>
                  ))}
                </nav>

                <div className="flex flex-col gap-2 pt-4 border-t border-secondary-200">
                  {user ? (
                    <>
                      <p className="text-sm text-secondary-600">Logged in as: {user.phone}</p>
                      {user.role === 'admin' && (
                        <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="outline" className="w-full justify-start">
                            <Settings className="mr-2 h-4 w-4" />
                            Admin Panel
                          </Button>
                        </Link>
                      )}
                      <Button variant="outline" onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full justify-start">
                        <LogOut className="mr-2 h-4 w-4" />
                        Logout
                      </Button>
                    </>
                  ) : (
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full justify-start">
                        <User className="mr-2 h-4 w-4" />
                        Login
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
